import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { StudioActionError } from '../StudioActionError';
import { useStudioActionFeedback } from '../useStudioActionFeedback';
import { useApproveReelMutation } from '../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { usePromoteChannels } from '../../hooks/usePromoteChannels';
import { SerializedReel, SocialPlatform } from '../../types';
import { canChannelTakeReel, channelNeedsSignIn, defaultReelPlatforms, REEL_CAPTION_MAX } from './reelReview';
import { reelConflictCode, ReelConflictCode } from './reelRefusal';
import { reelApproveMessages } from './reelReviewMessages';
import { ReelChannelChips } from './ReelChannelChips';
import { ReelSheetReel } from './ReelSheetReel';
import { ReelCaptionField } from './ReelCaptionField';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface ReelApproveSheetProps {
  isOpen: boolean;
  accountId: number;
  reel: SerializedReel;
  releaseTitle?: string;
  onClose: () => void;
  /** The reel is approved; `count` is the number of draft posts (design d2). */
  onApproved: (count: number) => void;
  /** A 409: the sheet closes and the page loads the reel again (design d4). */
  onConflict: (code: ReelConflictCode) => void;
}

/** The body of the open sheet. It mounts on open, so every open starts from the AI caption. */
const ApproveForm: React.FC<Omit<ReelApproveSheetProps, 'isOpen'>> = (props) => {
  const { accountId, reel, releaseTitle, onClose, onApproved, onConflict } = props;
  const intl = useIntl();
  const router = useSMLRouter();
  const { channels, isLoading, error: channelsError, retry } = usePromoteChannels({ accountId });
  const [approveReel, { isLoading: isSaving, error }] = useApproveReelMutation();
  const approveFeedback = useStudioActionFeedback(
    'approve the AI reel',
    error !== undefined && reelConflictCode(error) === null ? error : undefined
  );
  const [caption, setCaption] = useState(reel.summary?.caption ?? '');
  const [picked, setPicked] = useState<SocialPlatform[] | null>(null);

  const shown = useMemo(() => channels.filter((c) => canChannelTakeReel(c) || channelNeedsSignIn(c)), [channels]);
  const platforms = useMemo(() => picked ?? defaultReelPlatforms(channels), [channels, picked]);
  const toggle = useCallback(
    (platform: SocialPlatform) =>
      setPicked(platforms.includes(platform) ? platforms.filter((p) => p !== platform) : [...platforms, platform]),
    [platforms]
  );

  const isTooLong = caption.length > REEL_CAPTION_MAX;
  const isEmpty = caption.trim() === '';
  const canSave = !isTooLong && !isEmpty && platforms.length > 0 && !isSaving;

  const save = useCallback(async () => {
    const result = await approveReel({ accountId, reelUuid: reel.uuid, caption, channelPlatforms: platforms });
    if (isSuccessfulResult(result)) {
      onApproved(result.data.posts.length);
      return;
    }
    const conflict = reelConflictCode(result.error);
    if (conflict !== null) {
      onConflict(conflict);
    }
  }, [accountId, approveReel, caption, onApproved, onConflict, platforms, reel.uuid]);

  const header = (
    <ModalHeader toggle={onClose}>
      {intl.formatMessage(reelApproveMessages.title)}
      <small className="d-block text-muted fw-normal mt-1">
        {withBrandWord(intl.formatMessage(reelApproveMessages.intro))}
      </small>
    </ModalHeader>
  );
  const cancel = (
    <SMLButton outline omitCap onClick={onClose}>
      {intl.formatMessage(reelApproveMessages.cancel)}
    </SMLButton>
  );

  if (!isLoading && channelsError === undefined && shown.length === 0) {
    return (
      <>
        {header}
        <ModalBody>
          <Alert color="info" className="mb-0">
            <strong>{intl.formatMessage(reelApproveMessages.noChannelTitle)}</strong>
            <br />
            {intl.formatMessage(reelApproveMessages.noChannelBody)}
          </Alert>
        </ModalBody>
        <ModalFooter>
          {cancel}
          <SMLButton color="primary" omitCap onClick={() => router.push(SMLRoutes.promoteChannels)}>
            {intl.formatMessage(reelApproveMessages.connect)}
          </SMLButton>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      {header}
      <ModalBody className="d-flex flex-column gap-3">
        <ReelSheetReel reel={reel} releaseTitle={releaseTitle} />
        <StudioActionError feedback={approveFeedback} />
        <ReelCaptionField caption={caption} onChange={setCaption} platforms={platforms} />
        <div className={styles.field}>
          <div className={styles.countRow}>
            <span className={styles.label}>{intl.formatMessage(reelApproveMessages.channels)}</span>
            <span className={styles.secHint}>{intl.formatMessage(reelApproveMessages.channelsHint)}</span>
          </div>
          {isLoading && <LoadingView />}
          {channelsError !== undefined && <ErrorView error={channelsError} retry={retry} />}
          {!isLoading && <ReelChannelChips channels={shown} picked={platforms} onToggle={toggle} />}
        </div>
      </ModalBody>
      <ModalFooter>
        <span className={styles.footNote}>{intl.formatMessage(reelApproveMessages.draftNote)}</span>
        {cancel}
        <SMLButton color="primary" omitCap disabled={!canSave} isLoading={isSaving} onClick={save}>
          {intl.formatMessage(reelApproveMessages.save, { count: platforms.length })}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

/**
 * The approve sheet (design d1, d3, d4). Save sends the caption and the picked channels and
 * makes one draft post per channel. A caption over 2200 characters, an empty caption or no
 * channel keeps Save off. A caption over the limit of a picked channel shows a note: the server
 * shortens the caption of that channel's post.
 */
const ReelApproveSheetComponent: React.FC<ReelApproveSheetProps> = (props) => {
  const { isOpen, accountId, reel, releaseTitle, onClose, onApproved, onConflict } = props;
  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className="sml-modal">
      <ApproveForm
        accountId={accountId}
        reel={reel}
        releaseTitle={releaseTitle}
        onClose={onClose}
        onApproved={onApproved}
        onConflict={onConflict}
      />
    </Modal>
  );
};

export const ReelApproveSheet = TypedMemo(ReelApproveSheetComponent);
